import {
  useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';

import useLanguage from 'hooks/useLanguage';
import { Mixpanel } from 'mixpanel';
import useFocusedModule from 'hooks/useFocusedModule';
import useFileProductFinder from 'api/hooks/useFileProductFinder';
import useProductFinder from 'api/hooks/useProductFinder';
import useFileProductFinderPlants from 'api/hooks/useFileProductFinderPlants';
import Select from 'components/Select';
import DownloadModal from 'components/DownloadModal';
import { ProductPlantProps, SelectProps } from 'utils/types';

import mapSheetSvg from '/mapSheet.svg';
import downloadSvg from '/downloadGray.svg';
import truckSvg from '/truck.svg';
import trainSvg from '/train.svg';
import shipSvg from '/ship.svg';

import './index.css';
import useUser from 'api/hooks/useUser';

const ProductFinder = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<SelectProps[] | null>(null);
  const [application, setApplication] = useState<SelectProps[] | null>(null);
  const [downloadPdf, setDownloadPdf] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  const [files, setFiles] = useState<JSZip.JSZipObject[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState(false);

  const { data: productFiles, isFetching } = useProductFinder();

  const { data: products } = useFileProductFinder({ type: 'finder_products' });
  const { data: plants } = useFileProductFinderPlants();
  const { blueLang } = useLanguage();
  const { data: user } = useUser();

  useFocusedModule('Product Finder Module Duration');

  useEffect(() => {
    if (user) {
      Mixpanel.track('Product Finder Opened', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }
  }, [user]);

  useEffect(() => {
    if (blueLang.length > 0) {
      i18n.changeLanguage(blueLang);
    }
  }, [blueLang]);

  const fetchZip = async () => {
    const zip = new JSZip();

    try {
      const files = await zip.loadAsync(productFiles);

      return files.files;
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isFetching) {
      return;
    }

    setIsFilesLoading(true);

    fetchZip().then((data) => {
      if (!data) {
        return;
      }

      const cleanFiles = Object.values(data).reduce(
        (acc: JSZip.JSZipObject[], file) => {
          acc.push(file);

          return acc;
        },
        [],
      );

      setFiles(cleanFiles);
      setIsFilesLoading(false);
    });
  }, [isFetching]);

  const data = useMemo(() => products?.map((product) => {
    const plant = plants?.find(
      (plant) => plant['Plant ID'] === product['Plant ID'],
    );

    return {
      ...product,
      ...plant,
    } as ProductPlantProps;
  }), [plants, products]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'Product ID',
        header: '',
        cell: (cell: any) => {
          const selectedProduct = data?.find((prospect) => prospect['Product ID'] === cell.getValue()) || null;

          return (
            <div className="flex items-center">
              <button
                className={`md:w-[40px] mr-3 ${isFetching ? 'cursor-not-allowed' : 'cursor-pointer'} ${isFetching && 'opacity-50'}`}
                disabled={isFetching}
                onClick={() => {
                  if (isFetching) {
                    return;
                  }

                  const selectedProduct =
                    data?.find(
                      (prospect) => prospect['Product ID'] === cell.getValue(),
                    ) || null;

                  if (selectedProduct) {
                    setSelectedProduct(selectedProduct.Brand);
                    setDownloadPdf(true);
                    Mixpanel.track('Product Finder Download Clicked', {
                      role: user?.role?.name,
                      organization: user?.organization,
                      brand: selectedProduct.Brand,
                    });
                  }
                }}
              >
                <img src={downloadSvg} alt="download" />
              </button>
              <div
                onClick={() => {
                  Mixpanel.track('Product Finder Google Map Button Clicked', {
                    role: user?.role?.name,
                    organization: user?.user?.organization,
                  });
                }}
                className="cursor-pointer md:w-[40px]"
              >
                <a href={`https://www.google.com/maps/search/?api=1&query=${selectedProduct?.Lattitude}%2C${selectedProduct?.Longitude}`} target="_blank" rel="noreferrer">
                  <img src={mapSheetSvg} alt="map" />
                </a>
              </div>
            </div>
          );
        },
        footer: 'id',
      },
      {
        accessorKey: 'Plant City',
        header: t('plantCity'),
        footer: 'plantCity',
      },
      {
        accessorKey: 'Plant',
        header: t('plant'),
        footer: 'Plant',
        cell: (cell) => {
          if (cell.getValue()) {
            return cell.getValue().toString().replace('.', ',');
          }

          return '';
        },
      },
      {
        accessorKey: 'Brand',
        header: t('brand'),
        footer: 'brand',
      },
      {
        accessorKey: 'GIN Index',
        header: t('ginIndex'),
        footer: 'ginIndex',
      },
      {
        accessorKey: 'Product Name',
        header: t('productName'),
        footer: 'productName',
      },
      {
        accessorKey: 'Application',
        header: t('application'),
        footer: 'application',
      },
      {
        accessorKey: 'Packaging',
        header: t('packaging'),
        footer: 'packaging',
      },
      {
        accessorKey: 'Prod.Class-Text ?',
        header: t('prodClass'),
        footer: 'prodClass',
        cell: () => {
          const selectedProduct =
            data?.find((prospect) => prospect['Prod.Class-Text ?']) || null;

          return selectedProduct?.['Prod.Class-Text ?'];
        },
      },
      {
        accessorKey: 'Plant ID',
        header: t('transport'),
        cell: (cell: any) => {
          const getSelected = data?.find(
            (prospect) => prospect['Plant ID'] === cell.getValue(),
          );

          if (getSelected) {
            return (
              <div className="flex items-center justify-center">
                {getSelected['Truck Y/N'] === 'Yes' && (
                  <div className="md:w-[40px] mr-1">
                    <img src={truckSvg} alt="edit" />
                  </div>
                )}
                {getSelected['Train Y/N'] === 'Yes' && (
                  <div className="md:w-[40px] mr-1">
                    <img src={trainSvg} alt="edit" />
                  </div>
                )}
                {getSelected['Boat Y/N'] === 'Yes' && (
                  <div className="md:w-[40px]">
                    <img src={shipSvg} alt="edit" />
                  </div>
                )}
              </div>
            );
          }

          return null;
        },
        footer: 'id',
      },
    ],
    [data, files],
  );

  const filteredData = useMemo(() => {
    let filteredData = data;

    if (brand?.length) {
      const brandValues = brand?.map((b) => b.value);

      filteredData = filteredData?.filter((field) => brandValues?.includes(field['Brand']),
      );
    }

    if (application?.length) {
      const applicationValues = application?.map((b) => b.value);

      filteredData = filteredData?.filter((field) => applicationValues?.includes(field['Application']),
      );
    }

    return filteredData;
  }, [brand, application, data]);

  const brandSelect = useMemo(() => {
    const result: string[] = [];
    data?.forEach((product) => {
      result.push(product['Brand']);
    });
    const unique = [...new Set(result)];

    return unique.map((uniqBrand) => ({
      value: uniqBrand,
      label: uniqBrand,
    }));
  }, [data]);

  const applicationSelect = useMemo(() => {
    const result: string[] = [];
    data?.forEach((product) => {
      result.push(product['Application']);
    });
    const unique = [...new Set(result)];

    return unique.map((uniqBrand) => ({
      value: uniqBrand,
      label: uniqBrand,
    }));
  }, [data]);

  const table = useReactTable({
    columns,
    data: filteredData || [],
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="px-[100px] py-[32px] md:px-[16px] md:pb-4">
      <div className="mb-8">
        <span
          onClick={() => navigate('/')}
          className="text-secondaryText cursor-pointer"
        >
          {t('home')} /
        </span>
        <span className="font-medium"> {t('productFinder')}</span>
      </div>
      {/* <div className="text-3xl font-bold ">{t('productFinder')}</div> */}
      <div className="flex justify-between mb-6 md:flex-col">
        <div className="border border-primitiveGrey p-8 rounded-2xl mb-6 flex flex-col md:p-4 w-full">
          <div className="text-black font-bold mb-8 text-xl md:text-lg md:mt-2">
            {t('filters')}
          </div>
          <div className="flex md:flex-col mb-8">
            <div className="w-full mb-1 md:mb-4 mr-6">
              <Select
                value={brand as SelectProps[]}
                options={brandSelect}
                placeholder={t('selectValue')}
                title={t('brand')}
                onChange={setBrand}
                isMulti
              />
            </div>
            <div className="w-full mb-1 md:mb-4">
              <Select
                value={application as SelectProps[]}
                options={applicationSelect}
                placeholder={t('selectValue')}
                title={t('application')}
                onChange={setApplication}
                isMulti
              />
            </div>
          </div>
          <div>
            <div className="md:overflow-scroll">
              <table className="w-full">
                <thead className="bg-[#FAFAFA] border-grey300">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="font-bold text-sm mb-4 text-black align-baseline pb-4 pt-4 text-center headerCell"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          style={{
                            width: cell.column.getSize(),
                          }}
                          key={cell.id}
                          className="pt-3 pb-3 pr-2 pl-2 text-center"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="h-4" />
            </div>
          </div>
        </div>
      </div>
      {downloadPdf && (
        <DownloadModal
          setModal={setDownloadPdf}
          files={files}
          isFilesLoading={isFilesLoading}
          selectedProduct={selectedProduct || ''}
        />
      )}
    </div>
  );
};

export default ProductFinder;
